import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { hasRole } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Clock, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import CoiDialog from "@/components/CoiDialog";

const SLOTS = [
  { key: "valutatore", label: "Valutatore", field: "valutatore_id" },
  { key: "riesaminatore", label: "Riesaminatore tecnico", field: "riesaminatore_id" },
  { key: "deliberante", label: "Deliberante", field: "deliberante_id" },
];
const NONE = "__none__";

/** "Gruppo di valutazione" section of the assessment page (Blocco 1.3). */
export default function AssessmentTeamPanel({ assessmentId }) {
  const { user } = useAuth();
  const isAdmin = hasRole(user, "admin");
  const [team, setTeam] = useState(null);
  const [coiText, setCoiText] = useState("");
  const [candidates, setCandidates] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [coiItem, setCoiItem] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/assessments/${assessmentId}/team`);
    setTeam(data.team);
    setCoiText(data.coi_text);
    setForm(Object.fromEntries(SLOTS.map((s) => [s.field, data.team[s.key]?.legacy ? NONE : (data.team[s.key]?.user_id || NONE)])));
  }, [assessmentId]);

  useEffect(() => { load().catch(() => {}); }, [load]);
  useEffect(() => {
    if (isAdmin) api.get("/team/candidates").then(({ data }) => setCandidates(data)).catch(() => {});
  }, [isAdmin]);

  async function save() {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === NONE ? null : v]));
      await api.put(`/assessments/${assessmentId}/team`, payload);
      toast.success("Gruppo di valutazione salvato. Ogni persona assegnata dovrà confermare la dichiarazione di assenza di conflitti.");
      await load();
      window.dispatchEvent(new Event("coi:changed"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  }

  if (!team) return null;
  return (
    <Card className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6" data-testid="assessment-team-panel">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-5 h-5 text-viva-600" />
        <h3 className="font-heading text-lg font-semibold text-slate-900">Gruppo di valutazione</h3>
      </div>
      <p className="text-xs text-slate-500 mb-5">
        Separazione delle funzioni (ISO/IEC 17021-1 · 17029): valutatore, riesaminatore e deliberante devono essere persone diverse.
      </p>
      <div className="grid md:grid-cols-3 gap-4">
        {SLOTS.map((s) => {
          const m = team[s.key] || {};
          const confirmed = m.coi?.confirmed;
          const mine = m.user_id === user?.id && !m.legacy;
          return (
            <div key={s.key} className="rounded-xl border border-slate-200 p-4" data-testid={`team-slot-${s.key}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">{s.label}</div>
              {isAdmin && candidates ? (
                <Select value={form[s.field] || NONE} onValueChange={(v) => setForm({ ...form, [s.field]: v })}>
                  <SelectTrigger className="h-10 rounded-lg" data-testid={`team-select-${s.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— Non assegnato —</SelectItem>
                    {(candidates[s.key] || []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-sm font-medium text-slate-900" data-testid={`team-name-${s.key}`}>{m.name || (m.user_id ? "Utente non più presente" : "— Non assegnato —")}</div>
              )}
              {m.user_id && (
                m.legacy ? (
                  <div className="mt-2 text-xs text-amber-700" data-testid={`team-legacy-${s.key}`}>Assegnazione precedente al nuovo modello: da riconfermare</div>
                ) : confirmed ? (
                  <div className="mt-2 text-xs text-emerald-700 flex items-center gap-1" data-testid={`team-coi-ok-${s.key}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Dichiarazione confermata il {new Date(m.coi.confirmed_at).toLocaleString("it-IT")}
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-amber-700 flex items-center gap-1" data-testid={`team-coi-pending-${s.key}`}>
                    <Clock className="w-3.5 h-3.5" /> In attesa di dichiarazione conflitti
                  </div>
                )
              )}
              {mine && !confirmed && (
                <Button size="sm" onClick={() => setCoiItem({ assessment_id: assessmentId, slot: s.key, slot_label: s.label })}
                        data-testid={`team-coi-confirm-${s.key}`} className="mt-3 h-8 bg-amber-600 hover:bg-amber-700 text-white">
                  Conferma dichiarazione
                </Button>
              )}
            </div>
          );
        })}
      </div>
      {isAdmin && (
        <div className="mt-5 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="team-save-button" className="bg-viva-500 hover:bg-viva-600 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva gruppo di valutazione"}
          </Button>
        </div>
      )}
      <CoiDialog item={coiItem} text={coiText} onClose={() => setCoiItem(null)}
                 onDone={() => { load(); window.dispatchEvent(new Event("coi:changed")); }} />
    </Card>
  );
}
